import { CancellationError, CommandProps } from '@fluidx/office-fluid-types';
import type {
    DriverInfo,
    LoadedInfo,
    LoadInIframeResult,
    LoadInSourcelessIframeResult,
    OfficeWebHostScope,
} from '@ms/office-web-host';
import type { PerformanceDatapoint } from 'owa-analytics';
import { getSessionId } from 'owa-config';
import { timestamp } from 'owa-datetime';
import {
    fluidComponentCancelled,
    fluidComponentError,
    fluidComponentLoaded,
} from 'owa-fluid-datapoints';
import { reportPerfCheckmark, OWAFluidCheckmarksEnum } from 'owa-fluid-perf-datapoints';
import { FluidLoadOwaScenario, FluidLoadSource, FluidOwaSource } from 'owa-fluid-validations';
import * as trace from 'owa-trace';
import { getDriverDetailsForFluidItem } from './getDriverDetailsForFluidItem';
import { renderFluidComponent } from '../../components/FluidComponent';
import { getFluidPropsForRender } from '../fluidProps/getFluidPropsForRender';
import {
    getNotificationServicePromise,
    getSharedScope,
    getShareServicePromise,
} from '../fluidProps/scopeHelpers';
import { getFluidScenarioName } from './getFluidScenarioName';
import { getFluidScenarioEntryPoint } from './getFluidScenarioEntryPoint';

// Generic method to mount fluid components where the caller handles the insertion and
// the clean up. Used for fluid in any surface besides Editor, such as readingPane, calendar, and spaces
export async function mountFluidComponentExternal(
    targetDiv: HTMLDivElement,
    driverInfo: DriverInfo,
    scenarioStartTime: number,
    perfDatapoint: PerformanceDatapoint,
    owaSource: FluidOwaSource,
    headerNode: Node,
    url: string,
    controlNode?: React.ReactElement<{}>,
    customMenuItems?: CommandProps[],
    addRecipientFunc?: (address: string) => void,
    autoFocus?: boolean,
    viewStateId?: string
): Promise<LoadedInfo | undefined> {
    const scenarioLifecycle = 'Load';
    const scenarioName = getFluidScenarioName(owaSource);
    const scenarioEntryPoint = getFluidScenarioEntryPoint(owaSource);

    perfDatapoint.addCustomData({
        differenceFromStartTime: timestamp() - scenarioStartTime,
        owaSource: owaSource,
    });

    // Get OWA's session Id from config
    const sessionId = getSessionId();
    const mountComponentProps = getFluidPropsForRender(
        sessionId,
        owaSource,
        scenarioLifecycle,
        scenarioName,
        scenarioEntryPoint,
        scenarioStartTime,
        undefined /* codeDetails */,
        url,
        undefined /* deleteNode */,
        customMenuItems
    );

    let loadedInfo;
    let result: LoadInIframeResult | null = null;
    try {
        // We need to resolve the driverDetails for an already existing fluid component
        // after which we can instantiate the scope object.
        const driverDetails = getDriverDetailsForFluidItem(driverInfo.documentUrl);
        perfDatapoint.addCheckmark('fetchedDriverDetails');
        reportPerfCheckmark(OWAFluidCheckmarksEnum.driverDetailsFetched, owaSource);
        if (!driverDetails) {
            trace.errorThatWillCauseAlert(
                'Could not create driver details for existing fluid item'
            );
            throw new Error('failed to create driver details');
        }

        const scopeBase = getSharedScope(mountComponentProps.scopeProps);
        const scope: OfficeWebHostScope = {
            NotificationServicePromise: getNotificationServicePromise(
                mountComponentProps.scopeProps.tokenProvider,
                driverDetails.itemId,
                driverDetails.driveId,
                driverDetails.siteUrl,
                mountComponentProps.hostConfig.logger,
                sessionId,
                addRecipientFunc
            ),
            ShareServicePromise: getShareServicePromise(
                mountComponentProps.scopeProps.tokenProvider,
                mountComponentProps.hostConfig.logger,
                driverDetails.itemId,
                driverDetails.driveId,
                driverDetails.siteUrl,
                addRecipientFunc
            ),
            ...scopeBase,
        };

        result = await new Promise(resolve => {
            renderFluidComponent(
                mountComponentProps,
                scope,
                targetDiv,
                driverInfo,
                headerNode,
                resolve,
                owaSource,
                false, // isCreateNew
                controlNode,
                undefined, //heroComponentConfiguration
                autoFocus,
                viewStateId
            );
        });
        loadedInfo = await result!.loadedInfo;
        perfDatapoint.addCheckmark('fetchedLoadedInfo');
        reportPerfCheckmark(OWAFluidCheckmarksEnum.componentAndContainerBootStarted, owaSource);
        await Promise.all([loadedInfo.container, loadedInfo.component]);
        perfDatapoint.addCheckmark('componentAndContainerResolved');
        reportPerfCheckmark(OWAFluidCheckmarksEnum.componentAndContainerResolved, owaSource);
        reportPerfCheckmark(OWAFluidCheckmarksEnum.fluidFileLoaded, owaSource);
        fluidComponentLoaded(
            FluidLoadOwaScenario.ReadingPane,
            owaSource,
            FluidLoadSource.Fluid,
            scenarioStartTime
        );
        perfDatapoint.end();
    } catch (error) {
        // If the getIframe method is defined, but it returns undefined that means that the component has been unmounted or disposed
        // In this case we should treat any error like a cancellation error.
        const isIFrameUnmounted =
            (result as LoadInSourcelessIframeResult)?.getIframe &&
            !(result as LoadInSourcelessIframeResult).getIframe();
        if (error instanceof CancellationError || isIFrameUnmounted) {
            fluidComponentCancelled(FluidLoadOwaScenario.ReadingPane, owaSource, scenarioStartTime);
            perfDatapoint.invalidate();
            return undefined;
        }
        fluidComponentError(
            FluidLoadOwaScenario.ReadingPane,
            owaSource,
            FluidLoadSource.Fluid,
            error
        );
        perfDatapoint.endWithError(error);

        // We rethrow the error in case the consumer wants to use it.
        throw error;
    }

    return loadedInfo;
}
